import React, { useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import { RouterProps, useParams } from 'react-router';
import { compose } from 'redux';
import { Typography } from '@mui/material';
import { createEvent } from 'api';
import { AppStoreState, FilterGroup, LiteResource, WorkbookState } from 'types';
import { filterResources } from '../../../util/resource-util';
import ResourcesList from '../../../components/Resources/ResourcesList/ResourcesList';
import { Resource, UserFavorite } from 'types';
import { getSwitchFrameType } from '../../../util/resources-util';

import {
  setResourceFilters,
  toggleTag,
  selectResource,
  toggleResourcePlayerVisibility,
  updateAppCounter
} from 'state/modules/app';
import { resetResources } from 'state/modules/entity/custom/resource-favorites-mutations/actions';
import {
  fetchResources,
  fetchFavoriteResources,
  removeUserFavorite,
  fetchFilterTags
} from 'state/modules/entity/custom/resource-favorites-mutations/thunks/resource-thunks';
import withModal from '@lwtears/lwt-common-frontend/lib/Modal/withModal';
import { UseModalDispatch, UseModalState } from '@lwtears/lwt-common-frontend/lib/Modal';
import withStyles from 'components/@layout/withStyles';
import ResourceModal from 'components/@common/Modals/ResourcePlayer';
import ResourceFilterBreadcrumbs from '../../../components/Resources/ResourceFilterBreadcrumbs';
import { getCurrentWorkbook } from 'state/modules/entity/selectors/workbook-selectors';
import { hasClickAwayOnly } from 'state/modules/user/selectors/user-selectors';

type Props = {
  favoriteResources: Array<UserFavorite>;
  filterGroups: Array<FilterGroup>;
  locale: string;
  selectedFavoriteFilterTags: Array<number>;
  searchFavoriteText: string;
  selectedResource: LiteResource;
  showResourcePlayer: boolean;
  workbooksById: WorkbookState;
  selectResource: (resource: LiteResource) => { data: LiteResource };
  toggleResourcePlayerVisibility: (show: boolean) => void;
  setFilters: (
    searchText?: string | null,
    tagIds?: Array<number>,
    resourcesSection?: string
  ) => void;
  toggleTag: (tagId: number) => void;
  resetResources: () => void;
  updateCounter: (operationType: string) => number;
  fetchResources: (bookId?: number) => Array<Resource>;
  fetchFavoriteResources: (productCurriculumId?: number) => Array<UserFavorite>;
  fetchFilterTags: (bookId?: number) => Array<FilterGroup>;
  removeUserFavorite: (favoriteId: number) => void;
  classes: any;
} & RouterProps &
  UseModalDispatch &
  UseModalState;

const MyStuff = (props: Props) => {
  const params = useParams();
  const currentWorkbook = useSelector(state => getCurrentWorkbook(state, params));
  const isClickAwayOnly = useSelector(state => hasClickAwayOnly(state));

  useEffect(() => {
    fetchData();
  }, [props.locale, currentWorkbook?.id]);

  const getFilterTags = currentWorkbook => {
    if (currentWorkbook?.id) {
      props.fetchFilterTags(currentWorkbook?.id);
    }
  };

  const getFavoriteResources = currentWorkbook => {
    if (currentWorkbook?.products?.length) {
      props.fetchFavoriteResources(currentWorkbook?.products[0]?.productCurriculumId);
    }
  };

  const fetchData = () => {
    props.resetResources();
    props.updateCounter('reset');
    if (currentWorkbook?.id) {
      getFilterTags(currentWorkbook);
      if (!isClickAwayOnly && Object.entries(props.workbooksById).length > 0) {
        props.fetchResources(currentWorkbook.id);
      }
      getFavoriteResources(currentWorkbook);
    }
  };

  const handleSelectResource = async (resource: LiteResource) => {
    await props.selectResource(resource);
    props.toggleResourcePlayerVisibility(true);
    openModal(resource);
  };

  const openModal = (resource: LiteResource) => {
    const frameType = getSwitchFrameType(resource.type, resource.mediaType);
    props.openDraggableModal({
      titleText: resource.title,
      body: (
        <ResourceModal
          selectedResource={resource}
          frameType={frameType}
          handleClose={props.handleOnClose}
          eventLogger={() => createTelemetryEvent(resource)}
        />
      ),
      options: { closeOnBackdropClick: true },
      getPaperStyles: defaultStyles => ({
        ...defaultStyles,
        maxHeight: '85vh'
      })
    });
  };

  const handleRemoveFavorite = (resource: Resource) => {
    const favorite = props.favoriteResources.filter(favorite =>
      favorite.clickAway ? favorite.clickAway === resource : favorite.resource === resource
    );
    props.removeUserFavorite(favorite[0].id);
  };

  const createTelemetryEvent = (resource: LiteResource) =>
    createEvent('accessResource', {
      section: 'pinnedResources',
      resourceId: resource.id,
      resourceType: resource.type.type
    });

  const {
    selectedFavoriteFilterTags,
    searchFavoriteText,
    favoriteResources,
    classes: { favoriteResourcesStyle }
  } = props;

  const resources = favoriteResources.map(({ clickAway, resource }) =>
    clickAway ? clickAway : resource
  );
  const filteredFavoriteResources = filterResources(
    resources,
    selectedFavoriteFilterTags,
    searchFavoriteText
  );

  return (
    <div className={favoriteResourcesStyle}>
      <br />
      <div style={{ display: 'flex', flex: 1 }}>
        <Typography variant="h4" style={{ marginLeft: '30px', marginRight: '15px' }}>
          Filtered By:
        </Typography>
        <ResourceFilterBreadcrumbs />
      </div>
      <ResourcesList
        resourcesItems={filteredFavoriteResources}
        isFavoritePage={true}
        onSelectItem={handleSelectResource}
        onRemoveItem={handleRemoveFavorite}
      />
    </div>
  );
};

const styles: any = {
  favoriteResourcesStyle: {
    height: '100%',
    width: '100%',
    overflow: 'auto'
  },
  side: {
    flex: 1,
    height: '100%',
    display: 'flex',
    overflow: 'auto'
  }
};

const mapStateToProps = (state: AppStoreState) => ({
  favoriteResources: state.entity.favoriteResources,
  filterGroups: state.entity.filterGroups,
  locale: state.app.locale,
  searchFavoriteText: state.app.resource.selectedFilters.searchText,
  selectedFavoriteFilterTags: state.app.resource.selectedFilters.selectedTags,
  selectedResource: state.app.resource.selectedResource,
  showResourcePlayer: state.app.resource.showResourcePlayer,
  workbooksById: state.entity.workbooksById
});

export default compose(
  withModal,
  withStyles(styles),
  connect(mapStateToProps, {
    setFilters: setResourceFilters,
    toggleTag,
    resetResources,
    updateCounter: updateAppCounter,
    fetchResources,
    fetchFavoriteResources,
    removeUserFavorite,
    fetchFilterTags,
    selectResource,
    toggleResourcePlayerVisibility
  })
)(MyStuff as any);
